/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { Children, cloneElement, useContext, useId, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import {
  IconAdd,
  IconCopy,
  IconCsv,
  IconDelete,
  IconDetails,
  IconEdit,
  IconExcel,
  IconExport,
  IconMenu,
  IconPurge,
  IconRestore,
  IconSettings,
  IconRecycle,
  IconPublishing,
  IconVersioning,
  IconView
} from '../icon/icon'
import { ButtonPrimary } from '../component/button'
import { Floating, Tooltip } from '../component/floating'
import { Link } from 'react-router-dom'
import { useIsMobile } from '../hooks/use-is-mobile'
import { TableMenuContext } from '../table-builder/table-columns'
import { MenuHeaderContext } from './menu-context'
import { MenuScrollFrame } from './menu-scroll'
import './menu.scss'

export const MenuItemHeader = ({ text, show }) =>
  <React.Fragment>
    {text}
  </React.Fragment>

export const MenuItemSeperator = ({ show }) => <React.Fragment />

////////////////////////////////////////////

// Props read off the element by <Menu>: Icon, text, show, disabled, title, pin,
// keepOnMobile, to, onClick. `title` says why an item is disabled.
// `compact` renders the item icon-only (larger, with a hover tooltip) — used for the
// inline row actions inside a table so a bare icon still shows its word on hover.
export const MenuItem = ({ Icon, text, compact }) => {
  if (compact) {
    return (
      <Tooltip label={text}>
        {Icon && <Icon size="lg" />}
        {/* The tooltip label lives in a floating element that is not in the DOM
            until hover, and the icon itself is aria-hidden, so a compact item
            would leave its <button> (see MenuIconButton) with an EMPTY
            accessible name -- for screen readers, for voice control ("click
            edit"), and for tests. A visually-hidden copy of the same word names
            the control whether or not the tooltip is showing.

            It lives here rather than as an aria-label on the button because
            this is the only place that reliably knows the word: the prebuilt
            MenuEdit / MenuView / MenuDelete wrappers supply `text` from INSIDE
            themselves, so the element MenuIconButton holds carries no `text`
            prop at all. */}
        <span className="visually-hidden">
          {text}
        </span>
      </Tooltip>
    )
  }

  return (
    <span className="text-nowrap">
      {Icon && <Icon />} {text}
    </span>
  )
}

// Prebuilt items forward every prop to MenuItem (so `pin`, `compact`, a `text`
// override, etc. all pass through). React 19 ignores defaultProps on function
// components, so pin-by-default is resolved by <Menu> via PIN_DEFAULT_* below.
export const MenuAdd = props => <MenuItem Icon={IconAdd} text="add" {...props} />
export const MenuEdit = props => <MenuItem Icon={IconEdit} text="edit" {...props} />
export const MenuView = props => <MenuItem Icon={IconView} text="view" {...props} />
export const MenuCopy = props => <MenuItem Icon={IconCopy} text="copy" {...props} />
export const MenuDetails = props => <MenuItem Icon={IconDetails} text="details" {...props} />
export const MenuRestore = props => <MenuItem Icon={IconRestore} text="restore" {...props} />
export const MenuPurge = props => <MenuItem Icon={IconPurge} text="purge" {...props} />
export const MenuPermanentlyDelete = props => <MenuItem Icon={IconPurge} text="permanently delete" {...props} />
export const MenuEmpty = props => <MenuItem Icon={IconPurge} text="empty" {...props} />
export const MenuDelete = props => <MenuItem Icon={IconDelete} text="delete" {...props} />
export const MenuRemove = props => <MenuItem Icon={IconDelete} text="remove" {...props} />
export const MenuRecycle = props => <MenuItem Icon={IconRecycle} text="recycle bin" {...props} />
export const MenuExport = props => <MenuItem Icon={IconExport} text="export" {...props} />
export const MenuExportCsv = props => <MenuItem Icon={IconCsv} text="export csv" {...props} />
export const MenuExportExcel = props => <MenuItem Icon={IconExcel} text="export excel" {...props} />
export const MenuTableSettingToggle = props => <MenuItem Icon={IconSettings} text="table settings" {...props} />
export const MenuVersioning = props => <MenuItem Icon={IconVersioning} text="versioning" {...props} />
export const MenuPublishing = props => <MenuItem Icon={IconPublishing} text="versioning" {...props} />

// The universally-common actions that pin inline by default. Pages can override
// any item with an explicit `pin` prop.
const PIN_DEFAULT_TYPES = new Set([MenuEdit, MenuView, MenuCopy, MenuDelete, MenuDetails, MenuRestore])
// Most pages express these as an ad-hoc <MenuItem Icon={IconCopy} text="copy">
// rather than the prebuilt component; those pass their Icon as a prop, so a common
// action icon pins by default too.
const PIN_DEFAULT_ICONS = new Set([IconEdit, IconView, IconCopy, IconDelete, IconDetails, IconRestore])

const isShown = child => child && child.props && child.props.show

// A card header positions its own actions cell, so the menu must not float inside
// it. `float-end` takes the box out of normal flow: the header cell it sits in
// collapses to zero height, and the dropdown — which anchors to that box — resolves
// against a rect at the far left of the card and opens off the left edge of the
// screen. That is the bug users saw with long titles on both desktop and mobile.
//
// Everywhere else keeps float-end: those call sites are not flex parents, so
// swapping in ms-auto would silently stop right-aligning them.
const menuAlignClass = (isInsideTable, isInsideHeader) => {
  if (isInsideTable) return 'justify-content-center'
  if (isInsideHeader) return ''
  return 'float-end'
}

// Common actions pin by default ONLY in a table row (inline icons) or a page
// header (buttons). Everywhere else — content cards, inline lists — collapses into
// the kebab. An explicit `pin` prop always wins.
const isPinned = (child, insideTable, insideHeader) => {
  if (child.props.pin !== undefined) return child.props.pin
  if (!insideTable && !insideHeader) return false
  if (PIN_DEFAULT_TYPES.has(child.type)) return true
  if (child.props.Icon && PIN_DEFAULT_ICONS.has(child.props.Icon)) return true
  return false
}

export const MenuLink = ({ Icon, text, to, disabled }) => {
  const Content = ({ text }) =>
    <React.Fragment>
      {Icon && <Icon className="pr-1" />}
      {text}
    </React.Fragment>

  return (
    <React.Fragment>
      {disabled
        ? <div className="dropdown-item text-muted" style={{ cursor: 'not-allowed' }}>
            <Content text={text} />
          </div>
        : <Link className="dropdown-item" style={{ cursor: 'pointer' }} to={to}>
            <Content text={text} />
          </Link>}
    </React.Fragment>
  )
}

////////////////////////////////////////////

// `squarePins` (a grid card's footer, node-actions.js `card`) shows the
// pinned items as square, icon-only buttons beside the ⋮, matching it, and
// keeps them at phone width too.
//
// `sentenceCase` opts a menu out of the kebab's default text-capitalize
// (Title Case on every word, which mangles multi-word items like "require
// sso: off" into "Require Sso: Off" and can't leave an acronym like SSO
// upper-cased). It's off by default so every existing menu keeps its current
// look; a menu that turns it on must author its own `text` values already in
// the case it wants shown (see provider-domain-actions.js's row menu).
export const Menu = ({ children, small, placement, forceDropdown, sentenceCase, squarePins }) => {
  const isMobile = useIsMobile()
  const isInsideTable = useContext(TableMenuContext)
  const isInsideHeader = useContext(MenuHeaderContext)

  let childArray = useMemo(() => Children.toArray(children).filter(isShown), [children])

  // Table rows (desktop): pinned actions render as inline icon buttons with hover
  // tooltips; the action column auto-sizes to fit them (see table-ag.js). Non-pinned
  // actions collapse into the kebab. On mobile — or when forced — everything
  // collapses into the kebab.
  if (isInsideTable && !isMobile && !forceDropdown) {
    return <MenuInlineIcons childArray={childArray} small={small} placement={placement} sentenceCase={sentenceCase} />
  }

  const shouldForceDropdown = forceDropdown || isMobile || isInsideTable

  // On a phone a header folds its buttons into the kebab so the row fits,
  // except a pinned item marked `keepOnMobile` (a page's primary action),
  // which stays a button beside it.
  const keepsButton = c => isMobile && !forceDropdown && !isInsideTable && !!c.props.keepOnMobile && isPinned(c, isInsideTable, isInsideHeader)

  const childArrayPin = shouldForceDropdown ? childArray.filter(keepsButton) : childArray.filter(c => isPinned(c, isInsideTable, isInsideHeader))
  const childArrayDropdown = shouldForceDropdown
    ? childArray.filter(c => !keepsButton(c))
    : childArray.filter(c => !isPinned(c, isInsideTable, isInsideHeader))

  const single = childArray.length === 1 && (!shouldForceDropdown || keepsButton(childArray[0]))

  return (
    <React.Fragment>
      {childArray.length === 1 && single && (squarePins ? <MenuSquareButton child={childArray[0]} small={small} /> : <MenuButton child={childArray[0]} small={small} />)}
      {childArray.length === 1 && !single && <MenuListDropdown childArray={childArray} small={small} placement={placement} sentenceCase={sentenceCase} />}
      {childArray.length > 1 &&
        <div className={`d-flex align-items-center ${menuAlignClass(isInsideTable, isInsideHeader)} ${small && !isInsideTable ? 'm-1' : ''}`}>
          {childArrayPin.length > 0 &&
            (squarePins
              ? childArrayPin.map((child, i) => <MenuSquareButton key={i} child={child} small={small} />)
              : <MenuListButton childArray={childArrayPin} small={small} />)}
          {childArrayDropdown.length > 0 && <MenuListDropdown childArray={childArrayDropdown} small={small} placement={placement} sentenceCase={sentenceCase} />}
        </div>}
    </React.Fragment>
  )
}

////////////////////////////////////////////////////
// Inline row actions (inside a table). The action column auto-sizes to the widest
// cell, so we render every pinned action inline and let the column grow/shrink;
// non-pinned actions go in the kebab.

const MenuInlineIcons = ({ childArray, small, placement, sentenceCase }) => {
  const pinned = childArray.filter(c => isPinned(c, true, false))
  const unpinned = childArray.filter(c => !isPinned(c, true, false))

  return (
    <div className="d-flex align-items-center justify-content-center h-100">
      {pinned.map((child, i) => <MenuIconButton key={i} child={child} small={small} />)}
      {unpinned.length > 0 && <MenuListDropdown childArray={unpinned} small={small} placement={placement} sentenceCase={sentenceCase} />}
    </div>
  )
}

// Every pinned edit/view/delete icon in every table is one of these. Its
// accessible name comes from the compact MenuItem it wraps (see above), which
// is the only place that knows the item's word for every kind of menu item.
const MenuIconButton = ({ child, small }) => {
  const history = useHistory()

  const handleClick = e => {
    e.stopPropagation()
    if (child.props.disabled) return
    if (child.props.to) {
      history.push(child.props.to)
    } else if (child.props.onClick) {
      child.props.onClick(e)
    }
  }

  const button = (
    <button
      type="button"
      disabled={child.props.disabled}
      title={child.props.title}
      className={`btn border ${small ? 'btn-sm' : ''} d-inline-flex align-items-center justify-content-center px-2 mx-1`}
      onClick={handleClick}>
      {cloneElement(child, { compact: true })}
    </button>
  )

  // `title` says why an action is unavailable. A disabled button gets no
  // pointer events, so the wrapper carries the title too for the hover tooltip.
  return child.props.title ? <span className="d-inline-flex" title={child.props.title}>{button}</span> : button
}

////////////////////////////////////////////////////
// A grid card's pinned action: a square, icon-only button beside the card's
// ⋮, the same size, border, radius and colours as it. Its aria-label and its
// tooltip name it; when it is disabled the tooltip says why (`title`), and it
// stays focusable (aria-disabled) so a keyboard reaches the reason too. Its
// click is its own: never the card's.

const MenuSquareButton = ({ child, small }) => {
  const history = useHistory()
  const reasonID = `menu-pin-reason-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`
  const { Icon, text, title, disabled } = child.props

  const handleClick = e => {
    e.stopPropagation()
    e.preventDefault()
    if (disabled) return
    if (child.props.to) {
      history.push(child.props.to)
    } else if (child.props.onClick) {
      child.props.onClick(e)
    }
  }

  const reason = disabled && title ? title : null

  return (
    <Tooltip label={reason || text} keepCase>
      <button
        type="button"
        aria-label={text}
        aria-disabled={disabled ? 'true' : undefined}
        aria-describedby={reason ? reasonID : undefined}
        className={`btn border ${small ? 'btn-sm' : ''} menu-square-btn ms-1 ${disabled ? 'menu-square-btn-disabled' : ''}`}
        onClick={handleClick}>
        {Icon && <Icon size="lg" />}
      </button>
      {reason &&
        <span id={reasonID} className="visually-hidden">
          {reason}
        </span>}
    </Tooltip>
  )
}

////////////////////////////////////////////////////

const MenuButton = ({ child, small }) => {
  const history = useHistory()

  const handleClick = e => {
    e.stopPropagation()
    if (child.props.to) {
      history.push(child.props.to)
    } else if (child.props.onClick) {
      child.props.onClick(e)
    }
  }

  let component = useMemo(() => cloneElement(child, {}), [child])

  const button = (
    <ButtonPrimary disabled={child.props.disabled} title={child.props.title} small={small} onClick={handleClick}>
      {component}
    </ButtonPrimary>
  )

  // `title` says why an action is unavailable. A disabled button gets no
  // pointer events, so the wrapper carries the title too for the hover tooltip.
  return child.props.title ? <span className="d-inline-flex" title={child.props.title}>{button}</span> : button
}

const MenuListButton = ({ childArray, small }) => {
  return (
    <React.Fragment>
      {childArray.length > 0 &&
        <React.Fragment>
          {Children.map(childArray, (child, i) => <MenuButton key={i} child={child} small={small} to={child.props.to} onClick={child.props.onClick} />)}
        </React.Fragment>}
    </React.Fragment>
  )
}

////////////////////////////////////////////////////

const MenuListDropdown = ({ childArray, small, placement, sentenceCase }) => {
  const isInsideTable = useContext(TableMenuContext)
  const [visible, updateVisible] = useState(false)

  const history = useHistory()

  const handleItemClick = (e, child) => {
    e.stopPropagation()

    if (child.props.to) {
      history.push(child.props.to)
    } else if (child.props.onClick) {
      child.props.onClick(e)
    }

    setTimeout(() => {
      updateVisible(false)
    }, 100)
  }

  const handleOpenChange = value => updateVisible(value)

  // The frame draws the border and corners and scrolls the list inside them
  // (menu-scroll.js), so the list itself is flush.
  const content = (
    <MenuScrollFrame>
      <ul className={`list-group list-group-flush ${sentenceCase ? '' : 'text-capitalize'}`}>
        {Children.map(childArray, (child, i) => {
          if (child.type.name === MenuItemHeader.name) {
            return <MenuListItemHeader child={child} small={small} />
          } else if (child.type.name === MenuItemSeperator.name) {
            return <MenuListItemSeperator child={child} />
          } else {
            return <MenuListItemValue child={child} small={small} onClick={handleItemClick} />
          }
        })}
      </ul>
    </MenuScrollFrame>
  )

  // The ⋮ toggle is a real button: it is named, reachable from the keyboard,
  // and -- inside a grid row -- its click is its own, never the row's
  // (table-builder/row-click.js).
  return (
    <Floating content={content} isOpen={visible} placement={placement} onOpenChange={handleOpenChange}>
      <div className="d-flex h-100">
        <button type="button" aria-label="More actions" aria-expanded={visible} className={`btn border ${small ? 'btn-sm' : ''} ${isInsideTable ? '' : 'ms-1'}`}>
          <IconMenu size="lg" />
        </button>
      </div>
    </Floating>
  )
}

// `action` marks an action (not a heading or a separator): the frame counts
// those for its "N more".
const MenuListItem = ({ children, disabled, title, onClick, action }) => {
  const handleClick = e => {
    if (!disabled) {
      onClick && onClick(e)
    }
  }

  return (
    <li className={`list-group-item border-0 ${disabled ? 'text-secondary' : 'edxr-menu edxr-pointer '}`} title={title} onClick={handleClick} data-menu-action={action ? '' : undefined}>
      {children}
    </li>
  )
}

const MenuListItemHeader = ({ child, small }) => {
  return (
    <MenuListItem disabled>
      {small
        ? <small>
            {cloneElement(child, {})}
          </small>
        : <span>
            {cloneElement(child, {})}
          </span>}
    </MenuListItem>
  )
}

const MenuListItemSeperator = () => {
  return (
    <MenuListItem>
      <div className="border" />
    </MenuListItem>
  )
}

const MenuListItemValue = ({ child, small, onClick }) => {
  const handleClick = e => onClick && onClick(e, child)

  return (
    <MenuListItem action disabled={child.props.disabled} title={child.props.title} onClick={handleClick}>
      {small
        ? <small>
            {cloneElement(child, {})}
          </small>
        : <span>
            {cloneElement(child, {})}
          </span>}
    </MenuListItem>
  )
}
